import React, { useEffect } from 'react'

import { Box, withCitrusTheme } from '@gousto-internal/citrus-react'
import { useDispatch } from 'react-redux'
import { browserHistory } from 'react-router'

import { RegularDeliveryPrompt } from '@features/my-gousto-page/src/components/UnsubscribedDashboard/sections/RegularDeliveryPrompt/RegularDeliveryPrompt'
import type { DashboardActionData } from '@library/api-customer-engagement-experience'
import { queueTrackingEvent } from '@library/tracking-integration'

import { setOneOffOrderOrigin } from 'actions/oneOffOrder'
import { client as clientRoutes } from 'config/routes'

import { useShowResubscriptionPrompt } from './useShowResubscriptionPrompt'

const REACTIVATE_SUBSCRIPTION_INTENT = 'reactivate_subscription'

// Copy is supplied locally — the confirmation screen isn't wired to the dashboard API.
const RESUBSCRIPTION_PROMPT_DATA: DashboardActionData = {
  title: 'Fancy a regular delivery?',
  subtitle: 'Subscribe for guaranteed regular deliveries. Skip or cancel anytime.',
  icon: 'resub',
  intent: REACTIVATE_SUBSCRIPTION_INTENT,
}

// Surface discriminator so these events are separable from the dashboard prompt's.
const PROMPT_SOURCE = 'order_confirmation_resubscription_prompt'

type ResubscriptionPromptEvent = {
  action: 'resubscription_prompt_displayed' | 'resubscription_prompt_clicked'
  properties: {
    source: typeof PROMPT_SOURCE
  }
}

const PromptWrapper = withCitrusTheme(Box, (theme) => ({
  marginTop: `${theme.spacing(2)}px`,
}))

export const ResubscriptionPrompt = () => {
  const dispatch = useDispatch()
  const shouldShow = useShowResubscriptionPrompt()

  // Clear on unmount (not on read, which would re-hide the prompt mid-visit) so the origin can't
  // leak to a later confirmation.
  useEffect(
    () => () => {
      dispatch(setOneOffOrderOrigin(null))
    },
    [dispatch],
  )

  useEffect(() => {
    if (shouldShow) {
      queueTrackingEvent<ResubscriptionPromptEvent>({
        action: 'resubscription_prompt_displayed',
        properties: {
          source: PROMPT_SOURCE,
        },
      })
    }
  }, [shouldShow])

  if (!shouldShow) {
    return null
  }

  const handleAction = () => {
    queueTrackingEvent<ResubscriptionPromptEvent>({
      action: 'resubscription_prompt_clicked',
      properties: {
        source: PROMPT_SOURCE,
      },
    })
    browserHistory.push(clientRoutes.resubscribe)
  }

  return (
    <PromptWrapper>
      <RegularDeliveryPrompt
        data={RESUBSCRIPTION_PROMPT_DATA}
        onAction={handleAction}
        variant="orderConfirmation"
      />
    </PromptWrapper>
  )
}
